/**
 * TIN & NPI Performance (APP Plus Insights report, 2026-09-12).
 *
 * Visual-first: participant TINs as ranked horizontal bars (composite by
 * default, or any clinical measure's rate), ACO composite as the reference
 * line; click a TIN to expand its clinicians as bars; click a clinician for
 * the measure-by-measure drawer. A table view carries the same data as a grid.
 *
 * Numbers: /api/acos/:id/performance/tin-npi — a READ-TIME roll-up over the
 * shadow practice's NPI lane (Phase 3 dedup rules, proven against the GROUP
 * lane). Per-row composite = the year's APP engine with that row's clinical
 * counts substituted; CAHPS and admin-claims stay at ACO level (they have no
 * TIN/NPI split), so composite differences are clinical-only by construction.
 * The pipeline is untouched; the ACO composite still comes from the GROUP lane.
 */
import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, ChevronDown, ChevronRight, ClipboardList, Compass, Download, ExternalLink, Search, Table2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useAco } from '@/contexts/AcoContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { logicForYear } from '@/lib/app-plus/years/registry';
import type { StoredSubmission } from '@/lib/app-plus/years/types';
import type { AppQualityScore } from '@/lib/app-plus-scoring';
import {
  useAcoTinNpiPerformance,
  type NpiPerformanceRow,
  type TinNpiMeasureCounts,
  type TinPerformanceRow,
} from '@/hooks/queries/useMipsQueries';
import { useAppPlusScoring } from './lib/useAppPlusScoring';
import { AcoHeader, NoAcoState, YearUnsupportedState } from './components/AcoHeader';

type ViewMode = 'explore' | 'bars' | 'table';
type ShowFilter = 'all' | 'below_qps' | 'below_aco' | 'qps' | 'no_claims';

interface ScoredMeasure {
  baseId: string;
  pipelineId: string;
  name: string;
  isInverse: boolean;
  counts: TinNpiMeasureCounts | null;
  /** CMS PR: met / (met + not). null when no performance cases. */
  rate: number | null;
  /** Favorable rate for bar length (inverse → 100 − rate). */
  favorable: number | null;
  dc: number | null;
  points: number | null;
  state: string | null;
  openGaps: number;
}

interface ScoredRow {
  key: string;
  kind: 'tin' | 'npi';
  id: string;          // tin or npi
  tin: string;
  label: string;
  sublabel: string;
  patients: number;
  npiCount?: number;
  qpStatus?: string | null;
  mustReportClass?: string | null;
  composite: number | null;
  delta: number | null;
  measures: ScoredMeasure[];
  /** Metric used for the bar (composite or a measure's favorable rate). */
  metric: number | null;
  metricLabel: string;
}

const pct = (v: number | null | undefined, d = 1) => (v == null || Number.isNaN(v) ? '—' : `${v.toFixed(d)}%`);
const num = (v: number | null | undefined, d = 1) => (v == null || Number.isNaN(v) ? '—' : v.toFixed(d));

function prOf(c: TinNpiMeasureCounts | null): number | null {
  if (!c) return null;
  const perf = c.met + c.not;
  return perf > 0 ? (c.met / perf) * 100 : null;
}
function dcOf(c: TinNpiMeasureCounts | null): number | null {
  if (!c || c.den <= 0) return null;
  return ((c.met + c.not + c.exc) / c.den) * 100;
}
function openGapsOf(c: TinNpiMeasureCounts | null, isInverse: boolean): number {
  if (!c) return 0;
  return Math.max(0, c.den - c.exc - (isInverse ? c.not : c.met));
}

/**
 * Bar colour. Composite metric: the QPS threshold is the line that matters —
 * green only when the row clears it; amber when it beats the ACO but not the
 * QPS; red when it drags the ACO down. Measure metric (no QPS per measure):
 * green ≥ ACO rate, amber within 5 pts, red further below.
 */
function barTone(value: number | null, opts: { qps: number | null; aco: number | null; isComposite: boolean }): string {
  if (value == null) return 'bg-slate-300';
  if (opts.isComposite && opts.qps != null) {
    if (value >= opts.qps) return 'bg-emerald-500';
    if (opts.aco != null && value >= opts.aco) return 'bg-amber-400';
    return 'bg-red-500';
  }
  if (opts.aco == null) return 'bg-slate-400';
  if (value >= opts.aco) return 'bg-emerald-500';
  if (value > opts.aco - 5) return 'bg-amber-400';
  return 'bg-red-500';
}

function csvEsc(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// ===========================================================================
// Explore view (2026-09-12, UX pass 2) — master–detail that owns its space.
// Left: TINs ranked by OPPORTUNITY (patients × points below the QPS — where
// closing gaps moves the ACO the most) on one shared axis with a single QPS
// rule; each row is a name, a bar and a score — nothing else. Right: the
// selected TIN (or clinician) with a large score, its measures and clinicians.
// Bars and Table remain for comparison.
// ===========================================================================

type ExploreSort = 'composite' | 'gaps' | 'name';

const THIN_N = 20; // CMS case minimum — below it a composite is not trustworthy

/** Opportunity = open gaps, in absolute numbers: patients across the measures who still need the favourable outcome. */
const totalOpenGaps = (r: ScoredRow) => r.measures.reduce((s, m) => s + m.openGaps, 0);
/** Roster supplied a real name (not a "TIN 123…" / "Unattributed" fallback). */
const hasRealName = (r: ScoredRow) => !/^(TIN|NPI) \d/.test(r.label) && !/^Unattributed/.test(r.label) && r.label !== 'NPI not mapped';

/** Shared-axis bar: value on 0–100. The pass zone / QPS rule are drawn ONCE by the list, not per row. */
function AxisBar({ value, pass, thin, height = 'h-6' }: { value: number | null; pass: boolean; thin?: boolean; height?: string }) {
  const w = value == null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <span className={cn('relative block w-full', height)}>
      <span
        className={cn('absolute inset-y-0 left-0 rounded-r-md transition-[width]', pass ? 'bg-emerald-600' : 'bg-slate-700', thin && 'bg-slate-300')}
        style={{ width: `${w}%` }}
      />
    </span>
  );
}

/** Score gauge for the right panel: 0–100 track, pass zone, QPS rule, ACO tick, value marker. */
function ScoreGauge({ value, qps, aco }: { value: number | null; qps: number | null; aco: number | null }) {
  const v = value == null ? null : Math.max(0, Math.min(100, value));
  return (
    <div className="relative mt-3 h-12 w-full">
      {/* Label row (top) — pills, never overlapped by the track or the fill. */}
      {aco != null && (
        <span className="absolute top-0 -translate-x-1/2 whitespace-nowrap rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800 shadow-sm" style={{ left: `${aco}%` }}>
          ACO {aco.toFixed(1)}
        </span>
      )}
      {qps != null && (
        <span className="absolute top-0 -translate-x-1/2 whitespace-nowrap rounded-full border border-slate-400 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-900 shadow-sm" style={{ left: `${qps}%` }}>
          QPS {qps}
        </span>
      )}
      {/* Track */}
      <div className="absolute inset-x-0 top-8 h-2.5 rounded-full bg-slate-200" />
      {qps != null && <div className="absolute top-8 h-2.5 rounded-r-full bg-emerald-100" style={{ left: `${qps}%`, right: 0 }} />}
      {v != null && <div className={cn('absolute top-8 h-2.5 rounded-full', qps != null && v >= qps ? 'bg-emerald-600' : 'bg-slate-700')} style={{ width: `${v}%` }} />}
      {/* Reference lines drawn AFTER the fill so they sit on top of it. */}
      {aco != null && <div className="absolute top-6 h-6 w-0 border-l-2 border-dashed border-sky-500" style={{ left: `${aco}%` }} title={`ACO composite ${aco.toFixed(2)}`} />}
      {qps != null && <div className="absolute top-6 h-6 w-0.5 bg-slate-900" style={{ left: `${qps}%` }} title={`QPS ${qps}`} />}
      {v != null && <div className="absolute top-[26px] h-5 w-5 -translate-x-1/2 rounded-full border-2 border-white bg-slate-900 shadow" style={{ left: `${v}%` }} />}
    </div>
  );
}

function ExploreView(props: {
  tins: ScoredRow[];
  npisByTin: Record<string, ScoredRow[]>;
  qps: number | null;
  aco: number | null;
  acoCalculated: Record<string, Partial<Record<string, { met: number; not: number }>>>;
  lane: string | null;
  dcThreshold: number | null;
  search: string;
  shadowPracticeId: number | null;
  openInPatients: (row: Pick<ScoredRow, 'kind' | 'id' | 'tin'>) => void;
  navigate: (to: string) => void;
}) {
  const { tins, npisByTin, qps, aco, acoCalculated, lane, dcThreshold, search, shadowPracticeId, openInPatients, navigate } = props;
  const [sort, setSort] = useState<ExploreSort>('composite');
  const [selectedTin, setSelectedTin] = useState<string | null>(null);
  const [selectedNpi, setSelectedNpi] = useState<string | null>(null);

  const q = search.trim().toLowerCase();
  const ranked = useMemo(() => {
    const list = tins.filter(t => !q || t.label.toLowerCase().includes(q) || t.tin.toLowerCase().includes(q)
      || (npisByTin[t.tin] ?? []).some(n => n.label.toLowerCase().includes(q) || n.id.toLowerCase().includes(q)));
    const thinLast = (a: ScoredRow, b: ScoredRow) => Number(a.patients < THIN_N) - Number(b.patients < THIN_N);
    // Small-sample TINs always sink to the bottom so they can't take a top or
    // bottom rank on a handful of patients.
    switch (sort) {
      case 'gaps': return [...list].sort((a, b) => totalOpenGaps(b) - totalOpenGaps(a) || (a.composite ?? 0) - (b.composite ?? 0));
      case 'name': return [...list].sort((a, b) => a.tin.localeCompare(b.tin));
      default: return [...list].sort((a, b) => thinLast(a, b) || (b.composite ?? -1) - (a.composite ?? -1) || b.patients - a.patients);
    }
  }, [tins, npisByTin, q, sort]);

  const current = ranked.find(t => t.tin === selectedTin) ?? ranked[0] ?? null;
  const currentNpis = useMemo(() => {
    const list = current ? (npisByTin[current.tin] ?? []) : [];
    const gaps = (r: ScoredRow) => r.measures.reduce((s, m) => s + m.openGaps, 0);
    return [...list].sort((a, b) => gaps(b) - gaps(a));
  }, [current, npisByTin]);
  const focus: ScoredRow | null = selectedNpi ? (currentNpis.find(n => n.id === selectedNpi) ?? null) : null;
  const panel = focus ?? current;

  const scored = tins.filter(t => t.composite != null);
  const meet = scored.filter(t => qps != null && t.composite! >= qps).length;
  // The TIN with the most open gaps — the biggest opportunity in absolute numbers.
  const mostGaps = [...tins].filter(t => totalOpenGaps(t) > 0).sort((a, b) => totalOpenGaps(b) - totalOpenGaps(a))[0] ?? null;

  const acoRateFor = (m: ScoredMeasure) => {
    const c = acoCalculated[m.baseId]?.[lane ?? ''];
    if (!c) return null;
    const perf = c.met + c.not;
    if (perf <= 0) return null;
    const r = (c.met / perf) * 100;
    return m.isInverse ? 100 - r : r;
  };

  const sortCaption: Record<ExploreSort, string> = {
    composite: 'Highest performance first. Small-sample TINs are listed last.',
    gaps: 'Most open gaps first — the practices with the most patients still needing a favourable outcome.',
    name: 'By TIN number.',
  };

  return (
    <div className="space-y-4">
      {/* The one sentence this report exists to say. */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <p className="text-base">
          <span className="text-2xl font-semibold tabular-nums">{meet}</span>
          <span className="text-muted-foreground"> of </span>
          <span className="text-2xl font-semibold tabular-nums">{scored.length}</span>
          <span className="text-muted-foreground"> TINs clear the QPS{qps != null ? ` of ${qps}` : ''}.</span>
        </p>
        {mostGaps && (
          <button type="button" onClick={() => { setSelectedTin(mostGaps.tin); setSelectedNpi(null); setSort('gaps'); }}
            className="text-sm text-muted-foreground hover:text-foreground">
            Biggest opportunity: <span className="font-medium text-foreground underline decoration-dotted underline-offset-2">{mostGaps.label}</span>
            {' — '}{totalOpenGaps(mostGaps).toLocaleString()} open gaps across {mostGaps.patients.toLocaleString()} patients
          </button>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(380px,5fr)_6fr]">
        {/* LEFT: ranked TINs on one shared axis */}
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between px-4 pt-3">
            <div>
              <h3 className="text-sm font-semibold">Participant TINs</h3>
              <p className="text-xs text-muted-foreground">{sortCaption[sort]}</p>
            </div>
            <Select value={sort} onValueChange={(v: ExploreSort) => setSort(v)}>
              <SelectTrigger className="h-8 w-[170px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="composite">Composite</SelectItem>
                <SelectItem value="gaps">Opportunity (open gaps)</SelectItem>
                <SelectItem value="name">TIN number</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Shared axis: the pass zone and the QPS rule are drawn once, spanning the list. */}
          <div className="relative mt-3">
            {/* Axis header: labels sit ABOVE the list, as pills, so bars never cover them. */}
            <div className="grid px-4 text-[11px] text-muted-foreground" style={{ gridTemplateColumns: '180px 1fr 56px' }}>
              <span />
              <span className="relative block h-7">
                <span className="absolute bottom-0 left-0">0</span>
                <span className="absolute bottom-0 right-0">100</span>
                {aco != null && (
                  <span className="absolute bottom-0 -translate-x-1/2 whitespace-nowrap rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-800 shadow-sm" style={{ left: `${aco}%` }}>
                    ACO {aco.toFixed(1)}
                  </span>
                )}
                {qps != null && (
                  <span className="absolute bottom-0 -translate-x-1/2 whitespace-nowrap rounded-full border border-slate-400 bg-white px-2 py-0.5 text-xs font-semibold text-slate-900 shadow-sm" style={{ left: `${qps}%` }}>
                    QPS {qps}
                  </span>
                )}
              </span>
              <span className="self-end text-right">Score</span>
            </div>

            <div className="relative max-h-[calc(100vh-400px)] overflow-y-auto">
              {/* Background layer: the pass zone (behind the bars). */}
              <div className="pointer-events-none absolute inset-y-0 grid w-full px-4" style={{ gridTemplateColumns: '180px 1fr 56px' }}>
                <span />
                <span className="relative block">
                  {qps != null && <span className="absolute inset-y-0 right-0 bg-emerald-50/80" style={{ left: `${qps}%` }} />}
                </span>
                <span />
              </div>
              {/* Foreground layer: the QPS rule and the ACO dashed line, drawn OVER the bars. */}
              <div className="pointer-events-none absolute inset-y-0 z-10 grid w-full px-4" style={{ gridTemplateColumns: '180px 1fr 56px' }}>
                <span />
                <span className="relative block">
                  {aco != null && <span className="absolute inset-y-0 w-0 border-l-2 border-dashed border-sky-500 drop-shadow-[0_0_1px_rgba(255,255,255,0.9)]" style={{ left: `${aco}%` }} />}
                  {qps != null && <span className="absolute inset-y-0 w-0.5 bg-slate-900" style={{ left: `${qps}%` }} />}
                </span>
                <span />
              </div>

              {ranked.length === 0 && <p className="relative p-8 text-center text-sm text-muted-foreground">Nothing matches.</p>}
              {ranked.map(t => {
                const thin = t.patients < THIN_N;
                const pass = t.composite != null && qps != null && t.composite >= qps;
                const active = current?.tin === t.tin;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => { setSelectedTin(t.tin); setSelectedNpi(null); }}
                    className={cn(
                      'relative grid w-full items-center gap-3 px-4 py-2 text-left transition-colors',
                      active ? 'bg-sky-50/70' : 'hover:bg-slate-50/80',
                    )}
                    style={{ gridTemplateColumns: '180px 1fr 56px' }}
                  >
                    <span className={cn('absolute inset-y-0 left-0 w-1', active ? 'bg-sky-500' : 'bg-transparent')} />
                    <span className="min-w-0">
                      {/* Left list shows the TIN NUMBER (owner 2026-09-12); the practice name lives on the right panel. */}
                      <span className={cn('block truncate text-[14px] font-semibold leading-tight tabular-nums', thin && 'text-slate-500')} title={hasRealName(t) ? t.label : undefined}>{t.tin}</span>
                      <span className="block text-[11px] text-muted-foreground">
                        {t.patients.toLocaleString()} patients · <span className={cn(sort === 'gaps' && 'font-semibold text-slate-800')}>{totalOpenGaps(t).toLocaleString()} open gaps</span>
                        {thin && <span className="ml-1.5 rounded bg-amber-50 px-1 text-[10px] text-amber-800">small sample</span>}
                      </span>
                    </span>
                    <AxisBar value={t.composite} pass={pass} thin={thin} />
                    <span className={cn('text-right text-[15px] font-semibold tabular-nums', pass ? 'text-emerald-700' : thin ? 'text-slate-400' : 'text-slate-900')}>
                      {num(t.composite, 1)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <p className="flex flex-wrap items-center gap-x-3 border-t px-4 py-2 text-[11px] text-muted-foreground">
            <span>A bar reaching the green zone clears the QPS.</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-0 border-l-2 border-dashed border-sky-600" /> ACO composite</span>
            <span>Small-sample TINs (under {THIN_N} patients) are faded and ranked last.</span>
          </p>
        </Card>

        {/* RIGHT: selected TIN or clinician */}
        <Card>
          {!panel ? (
            <CardContent className="p-10 text-center text-sm text-muted-foreground">Select a TIN.</CardContent>
          ) : (
            <CardContent className="p-5">
              {focus && (
                <button type="button" onClick={() => setSelectedNpi(null)} className="mb-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                  <ArrowLeft className="h-3 w-3" /> {current?.label}
                </button>
              )}
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="truncate text-2xl font-semibold leading-tight">{panel.label}</h2>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {panel.kind === 'npi'
                      ? <>NPI <span className="tabular-nums">{panel.id}</span>{current && hasRealName(current) && <> · {current.label}</>}</>
                      : <>TIN <span className="tabular-nums">{panel.tin}</span></>}
                    {' · '}{panel.patients.toLocaleString()} patients
                    {panel.kind === 'tin' && ` · ${currentNpis.length} clinician${currentNpis.length === 1 ? '' : 's'}`}
                    {panel.qpStatus && (panel.qpStatus === 'Y' || panel.qpStatus === 'P') && <Badge variant="outline" className="ml-2 h-5">QP</Badge>}
                    {panel.patients < THIN_N && <Badge variant="outline" className="ml-2 h-5 border-amber-300 text-amber-800">small sample</Badge>}
                  </p>
                </div>
                <div className="text-right">
                  <div className={cn('text-4xl font-bold leading-none tabular-nums', panel.composite != null && qps != null && panel.composite >= qps ? 'text-emerald-700' : 'text-slate-900')}>
                    {num(panel.composite, 1)}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {qps != null && panel.composite != null
                      ? (panel.composite >= qps
                        ? <span className="text-emerald-700">clears the QPS by {(panel.composite - qps).toFixed(1)}</span>
                        : <span className="text-red-700">{(qps - panel.composite).toFixed(1)} below the QPS</span>)
                      : 'composite'}
                  </div>
                </div>
              </div>
              <ScoreGauge value={panel.composite} qps={qps} aco={aco} />

              {/* Measures */}
              <div className="mt-6 flex items-baseline justify-between">
                <h3 className="text-sm font-semibold">Measures</h3>
                <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">bar = favourable rate · <span className="inline-block h-3 w-0 border-l-2 border-dashed border-sky-600" /> ACO rate</span>
              </div>
              {panel.measures.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">No claims in the {lane} lane.</p>
              ) : (
                <table className="mt-2 w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="py-1.5 pr-3 font-medium">ID</th>
                      <th className="py-1.5 pr-3 font-medium">Measure</th>
                      <th className="py-1.5 pr-3 font-medium">Performance</th>
                      <th className="py-1.5 pr-3 text-right font-medium">Rate</th>
                      <th className="py-1.5 pr-3 text-right font-medium" title={dcThreshold != null ? `Data completeness — ${dcThreshold}% required` : 'Data completeness'}>DC</th>
                      <th className="py-1.5 text-right font-medium">Open</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {panel.measures.map(m => {
                      const acoFav = acoRateFor(m);
                      const dcMiss = m.dc != null && dcThreshold != null && m.dc < dcThreshold;
                      const better = m.favorable != null && acoFav != null && m.favorable >= acoFav;
                      return (
                        <tr key={m.baseId}>
                          {/* Same ID treatment as QPS Management's measure column. */}
                          <td className="py-2 pr-4 text-xl font-black text-primary">{m.pipelineId}</td>
                          <td className="max-w-[220px] py-2 pr-3">
                            <span className="block truncate font-medium" title={m.name}>{m.name}</span>
                            {m.isInverse && <span className="block text-[11px] text-muted-foreground">lower is better</span>}
                          </td>
                          <td className="w-[38%] py-2 pr-3">
                            <span className="relative block h-4 w-full rounded-md bg-slate-100">
                              <span className={cn('absolute inset-y-0 left-0 rounded-md', better ? 'bg-emerald-600' : 'bg-slate-600')} style={{ width: `${m.favorable ?? 0}%` }} />
                              {acoFav != null && <span className="absolute inset-y-0 w-0 border-l-2 border-dashed border-sky-600" style={{ left: `${acoFav}%` }} />}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-right text-[15px] font-semibold tabular-nums">{pct(m.rate)}</td>
                          <td className={cn('py-2 pr-3 text-right text-[15px] tabular-nums', dcMiss ? 'font-semibold text-amber-700' : 'text-slate-600')}>{pct(m.dc, 0)}</td>
                          <td className="py-2 text-right text-[15px] tabular-nums text-slate-600">{m.openGaps.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Clinicians (TIN panel only) */}
              {panel.kind === 'tin' && (
                <>
                  <div className="mt-6 flex items-baseline justify-between">
                    <h3 className="text-sm font-semibold">Clinicians</h3>
                    <span className="text-[11px] text-muted-foreground">most open gaps first</span>
                  </div>
                  {currentNpis.length === 0 ? (
                    <p className="mt-2 text-sm text-muted-foreground">No clinicians with claims in this lane.</p>
                  ) : (
                    <div className="mt-2 divide-y rounded-lg border">
                      {currentNpis.map(n => {
                        const thin = n.patients < THIN_N;
                        const pass = n.composite != null && qps != null && n.composite >= qps;
                        const gaps = n.measures.reduce((s, m) => s + m.openGaps, 0);
                        return (
                          <button key={n.key} type="button" onClick={() => setSelectedNpi(n.id)}
                            className="grid w-full items-center gap-4 px-3 py-2.5 text-left hover:bg-sky-50/60" style={{ gridTemplateColumns: '1fr 140px 56px 20px' }}>
                            <span className="min-w-0">
                              <span className={cn('block truncate text-sm font-medium', thin && 'text-slate-500')}>
                                {n.label}{n.qpStatus && (n.qpStatus === 'Y' || n.qpStatus === 'P') && <Badge variant="outline" className="ml-1.5 h-4 px-1 text-[10px]">QP</Badge>}
                              </span>
                              <span className="block text-[11px] text-muted-foreground">
                                {n.patients.toLocaleString()} patients · {gaps.toLocaleString()} open gaps{thin && <span className="ml-1.5 rounded bg-amber-50 px-1 text-[10px] text-amber-800">small sample</span>}
                              </span>
                            </span>
                            <AxisBar value={n.composite} pass={pass} thin={thin} height="h-4" />
                            <span className={cn('text-right text-sm font-semibold tabular-nums', pass ? 'text-emerald-700' : thin ? 'text-slate-400' : 'text-slate-900')}>
                              {n.metricLabel === 'no claims' ? '—' : num(n.composite, 1)}
                            </span>
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              <div className="mt-6 flex flex-wrap gap-2">
                <Button size="sm" onClick={() => openInPatients(panel)}>
                  <ClipboardList className="mr-1.5 h-4 w-4" /> Open in Patients
                </Button>
                {panel.kind === 'npi' && shadowPracticeId && panel.id !== '0000000000' && panel.measures[0] && (
                  <Button size="sm" variant="outline" onClick={() => navigate(`/programs/app-plus/measure/${encodeURIComponent(`${panel.measures[0].pipelineId}:${lane}`)}?evaluationType=NPI&npi=${encodeURIComponent(panel.id)}&practiceId=${shadowPracticeId}`)}>
                    Detailed results <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}

export default function InsightsTinNpi() {
  const navigate = useNavigate();
  const { selectedAco, loading: acoLoading } = useAco();
  const { programYear } = useProgramYear();
  const scoring = useAppPlusScoring(selectedAco?.id ?? null, selectedAco?.shadow_practice_id ?? null);

  const logic = useMemo(() => { try { return logicForYear(programYear); } catch { return null; } }, [programYear]);
  const measureSet = useMemo(() => { try { return logic?.getMeasureSet(programYear) ?? null; } catch { return null; } }, [logic, programYear]);
  const constants = useMemo(() => { try { return logic?.getConstants(programYear) ?? null; } catch { return null; } }, [logic, programYear]);

  // Lanes the ACO actually has data for (from the GROUP summaries) → collection-type picker.
  const lanes = useMemo(() => {
    const set = new Set<string>();
    for (const byType of Object.values(scoring.calculated)) for (const k of Object.keys(byType ?? {})) set.add(k);
    const order = ['Medicare CQM', 'MIPS CQM', 'eCQM'];
    return Array.from(set).sort((a, b) => (order.indexOf(a) + 99) % 99 - (order.indexOf(b) + 99) % 99);
  }, [scoring.calculated]);

  const [laneChoice, setLaneChoice] = useState<string | null>(null);
  const lane = laneChoice && lanes.includes(laneChoice) ? laneChoice : (lanes[0] ?? null);

  const [view, setView] = useState<ViewMode>('bars');
  const [metric, setMetric] = useState<string>('composite');
  const [show, setShow] = useState<ShowFilter>('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [drawer, setDrawer] = useState<ScoredRow | null>(null);

  const perfQ = useAcoTinNpiPerformance(selectedAco?.id ?? null, programYear, lane, !!lane);
  const data = perfQ.data ?? null;

  // Clinical measure map for the lane: base id ↔ pipeline id (SSP twin for Medicare CQM).
  const clinical = useMemo(() => {
    if (!measureSet) return [];
    return measureSet.measures
      .filter(m => m.bucket === 'clinical')
      .map(m => ({
        baseId: m.measureId,
        pipelineId: lane === 'Medicare CQM' && m.sspVariantId ? m.sspVariantId : m.measureId,
        name: m.name,
        isInverse: m.isInverse,
      }));
  }, [measureSet, lane]);

  // ACO-level (GROUP lane) counts for the lane — the baseline the bars compare against.
  const acoScore = scoring.applied;
  const acoComposite = acoScore?.composite ?? null;

  const scoreRow = (
    kind: 'tin' | 'npi',
    id: string,
    tin: string,
    label: string,
    sublabel: string,
    patients: number,
    measures: TinNpiMeasureCounts[],
    extra: Partial<ScoredRow> = {},
  ): ScoredRow => {
    const byId = new Map(measures.map(m => [m.measureId, m]));
    // Synthesize clinical rows with this row's counts; keep ACO-level non-clinical rows.
    const rows: StoredSubmission[] = [];
    for (const [mid, entry] of Object.entries(scoring.effectiveEntries)) {
      if (!clinical.some(c => c.baseId === mid)) rows.push(entry);
    }
    for (const c of clinical) {
      const counts = byId.get(c.pipelineId) ?? null;
      rows.push({
        measure_id: c.baseId,
        collection_type: lane ?? 'MIPS CQM',
        reported: !!counts,
        eligible_population: counts?.ip ?? 0,
        denominator_exclusions: counts?.ex ?? 0,
        performance_met: counts?.met ?? 0,
        denominator_exceptions: counts?.exc ?? 0,
        performance_not_met: counts?.not ?? 0,
        payload: { mode: 'calc' },
      });
    }
    let engine: AppQualityScore | null = null;
    try { engine = logic ? logic.runEngine(rows, scoring.engineConfig, programYear) : null; } catch { engine = null; }
    const engineById = new Map((engine?.measures ?? []).map(m => [m.measureId, m]));

    const scored: ScoredMeasure[] = clinical.map(c => {
      const counts = byId.get(c.pipelineId) ?? null;
      const rate = prOf(counts);
      const res = engineById.get(c.baseId);
      return {
        baseId: c.baseId, pipelineId: c.pipelineId, name: c.name, isInverse: c.isInverse,
        counts, rate,
        favorable: rate == null ? null : (c.isInverse ? 100 - rate : rate),
        dc: dcOf(counts),
        points: res && (res.state === 'scored' || res.state === 'dc_miss') ? res.points : null,
        state: res?.state ?? null,
        openGaps: openGapsOf(counts, c.isInverse),
      };
    });
    const composite = engine?.composite ?? null;
    const delta = composite != null && acoComposite != null ? composite - acoComposite : null;

    let metricVal: number | null = composite;
    let metricLabel = composite != null ? composite.toFixed(1) : '—';
    if (metric !== 'composite') {
      const m = scored.find(s => s.baseId === metric);
      metricVal = m?.favorable ?? null;
      metricLabel = m?.rate != null ? `${m.rate.toFixed(1)}%${m.isInverse ? ' (inv)' : ''}` : '—';
    }
    return {
      key: `${kind}:${tin}:${id}`, kind, id, tin, label, sublabel, patients,
      composite, delta, measures: scored, metric: metricVal, metricLabel, ...extra,
    };
  };

  const tinRows = useMemo<ScoredRow[]>(() => {
    if (!data) return [];
    return data.tins.map(t => scoreRow(
      'tin', t.tin, t.tin,
      t.name || (t.tin === 'A0000' || t.tin === 'UNKNOWN' ? 'Unattributed TIN' : `TIN ${t.tin}`),
      `${t.npiCount} clinician${t.npiCount === 1 ? '' : 's'} · ${t.patients.toLocaleString()} patients`,
      t.patients, t.measures, { npiCount: t.npiCount },
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, clinical, lane, metric, scoring.effectiveEntries, scoring.engineConfig, acoComposite, logic, programYear]);

  const npiRowsByTin = useMemo<Record<string, ScoredRow[]>>(() => {
    if (!data) return {};
    const out: Record<string, ScoredRow[]> = {};
    for (const n of data.npis) {
      const row = scoreRow(
        'npi', n.npi, n.tin,
        n.name || (n.npi === '0000000000' ? 'NPI not mapped' : `NPI ${n.npi}`),
        `${n.npi !== '0000000000' ? n.npi + ' · ' : ''}${n.patients.toLocaleString()} patients`,
        n.patients, n.measures, { qpStatus: n.qpStatus, mustReportClass: n.mustReportClass },
      );
      (out[n.tin] ??= []).push(row);
    }
    for (const r of data.rosterOnly) {
      (out[r.tin] ??= []).push({
        key: `npi:${r.tin}:${r.npi}`, kind: 'npi', id: r.npi, tin: r.tin,
        label: r.name || `NPI ${r.npi}`, sublabel: `${r.npi} · no claims in this lane`,
        patients: 0, qpStatus: r.qpStatus, mustReportClass: r.mustReportClass,
        composite: null, delta: null, measures: [], metric: null, metricLabel: 'no claims',
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, clinical, lane, metric, scoring.effectiveEntries, scoring.engineConfig, acoComposite, logic, programYear]);

  const qpsThreshold = constants?.qpsThreshold ?? null;

  // Filters
  const q = search.trim().toLowerCase();
  const passesShow = (r: ScoredRow) => {
    switch (show) {
      case 'below_qps': return r.composite != null && qpsThreshold != null && r.composite < qpsThreshold;
      case 'below_aco': return r.delta != null && r.delta < 0;
      case 'qps': return r.kind === 'tin' || r.qpStatus === 'Y' || r.qpStatus === 'P';
      case 'no_claims': return r.kind === 'tin' || r.metricLabel === 'no claims';
      default: return true;
    }
  };
  const matches = (r: ScoredRow) => !q || r.label.toLowerCase().includes(q) || r.id.toLowerCase().includes(q) || r.tin.toLowerCase().includes(q);

  const visibleTins = useMemo(() => {
    const sortVal = (r: ScoredRow) => (r.metric == null ? -1 : r.metric);
    return tinRows
      .map(t => {
        const kids = (npiRowsByTin[t.tin] ?? []).filter(n => passesShow(n) && (matches(t) || matches(n)))
          .sort((a, b) => sortVal(b) - sortVal(a));
        return { tin: t, kids, tinHit: matches(t) };
      })
      .filter(({ tin, kids, tinHit }) => ((show === 'below_aco' || show === 'below_qps') ? passesShow(tin) || kids.length > 0 : true) && (tinHit || kids.length > 0 || !q))
      .filter(({ kids }) => show !== 'no_claims' || kids.length > 0)
      .sort((a, b) => sortVal(b.tin) - sortVal(a.tin));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tinRows, npiRowsByTin, show, q]);

  const totalNpis = useMemo(() => Object.values(npiRowsByTin).reduce((s, a) => s + a.length, 0), [npiRowsByTin]);
  const scaleMax = metric === 'composite' ? 100 : 100;
  const refLine = metric === 'composite'
    ? acoComposite
    : (() => {
        const c = clinical.find(x => x.baseId === metric);
        const counts = c ? scoring.calculated[c.baseId]?.[lane ?? ''] : undefined;
        if (!c || !counts) return null;
        const perf = counts.met + counts.not;
        const rate = perf > 0 ? (counts.met / perf) * 100 : null;
        return rate == null ? null : (c.isInverse ? 100 - rate : rate);
      })();

  const exportCsv = (level: 'tin' | 'npi') => {
    const header = level === 'tin'
      ? ['TIN', 'Practice', 'Clinicians', 'Patients', ...clinical.map(c => `${c.pipelineId} rate %`), ...clinical.map(c => `${c.pipelineId} DC %`), 'Composite', 'Delta vs ACO']
      : ['TIN', 'Practice', 'NPI', 'Clinician', 'QP', 'Patients', ...clinical.map(c => `${c.pipelineId} rate %`), ...clinical.map(c => `${c.pipelineId} DC %`), ...clinical.map(c => `${c.pipelineId} open gaps`), 'Composite', 'Delta vs ACO'];
    const lines = [header.map(csvEsc).join(',')];
    for (const t of visibleTins) {
      if (level === 'tin') {
        lines.push([t.tin.tin, t.tin.label, t.tin.npiCount ?? '', t.tin.patients,
          ...t.tin.measures.map(m => num(m.rate)), ...t.tin.measures.map(m => num(m.dc)),
          num(t.tin.composite, 2), num(t.tin.delta, 2)].map(csvEsc).join(','));
      } else {
        for (const n of t.kids) {
          lines.push([t.tin.tin, t.tin.label, n.id, n.label, n.qpStatus ?? '', n.patients,
            ...n.measures.map(m => num(m.rate)), ...n.measures.map(m => num(m.dc)), ...n.measures.map(m => m.openGaps),
            num(n.composite, 2), num(n.delta, 2)].map(csvEsc).join(','));
        }
      }
    }
    const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `app-plus-${level}-performance-${selectedAco?.aco_id_cms ?? 'aco'}-${programYear}-${(lane ?? 'all').replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  /** Insights → Patients handoff: pre-filter the worklist to this TIN / clinician + lane. */
  const openInPatients = (row: Pick<ScoredRow, 'kind' | 'id' | 'tin'>) => {
    const p = new URLSearchParams();
    if (data?.shadowPracticeId) p.set('practiceId', String(data.shadowPracticeId));
    if (lane) p.set('collectionType', lane);
    if (row.tin && row.tin !== 'UNKNOWN') p.set('tin', row.tin);
    if (row.kind === 'npi' && row.id !== '0000000000') p.set('npi', row.id);
    navigate(`/patients?${p.toString()}`);
  };

  if (!scoring.yearSupported) return <YearUnsupportedState year={programYear} />;
  if (acoLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!selectedAco) return <NoAcoState />;

  const loading = scoring.loading || (perfQ.isPending && !perfQ.data);

  const Bar = ({ row, depth }: { row: ScoredRow; depth: 0 | 1 }) => {
    const width = row.metric == null ? 0 : Math.max(0, Math.min(100, (row.metric / scaleMax) * 100));
    const isOpen = !!expanded[row.tin];
    const clickable = depth === 0 ? (npiRowsByTin[row.tin]?.length ?? 0) > 0 : true;
    return (
      <button
        type="button"
        onClick={() => depth === 0
          ? setExpanded(e => ({ ...e, [row.tin]: !e[row.tin] }))
          : setDrawer(row)}
        className={cn(
          'group grid w-full items-center gap-3 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-slate-50',
          depth === 1 && 'pl-8',
        )}
        style={{ gridTemplateColumns: depth === 0 ? '260px 1fr 116px' : '244px 1fr 116px' }}
        title={row.kind === 'npi' ? 'Open clinician detail' : isOpen ? 'Collapse clinicians' : 'Show clinicians'}
      >
        <span className="min-w-0">
          <span className="flex items-center gap-1.5">
            {depth === 0 && (clickable
              ? (isOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-500" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-500" />)
              : <span className="w-3.5" />)}
            <span className={cn('truncate', depth === 0 ? 'text-sm font-medium' : 'text-sm')}>{row.label}</span>
            {row.qpStatus && (row.qpStatus === 'Y' || row.qpStatus === 'P') && <Badge variant="outline" className="h-4 px-1 text-[10px]">QP</Badge>}
          </span>
          <span className="block truncate text-[11px] text-muted-foreground">{row.sublabel}</span>
        </span>
        <span className="relative h-5 w-full overflow-hidden rounded bg-slate-100">
          <span
            className={cn('absolute inset-y-0 left-0 rounded', barTone(row.metric, { qps: qpsThreshold, aco: refLine, isComposite: metric === 'composite' }), depth === 1 && 'opacity-80')}
            style={{ width: `${width}%` }}
          />
          {refLine != null && (
            <span className="absolute inset-y-0 w-0 border-l-2 border-dashed border-sky-600" style={{ left: `${Math.min(100, (refLine / scaleMax) * 100)}%` }} title={`ACO ${num(refLine)}`} />
          )}
          {metric === 'composite' && qpsThreshold != null && (
            <span className="absolute inset-y-0 w-0.5 bg-slate-900" style={{ left: `${Math.min(100, (qpsThreshold / scaleMax) * 100)}%` }} title={`QPS ${qpsThreshold}`} />
          )}
        </span>
        <span className="flex items-center justify-end gap-1 text-right text-sm tabular-nums">
          <span>
            <span className="font-semibold">{row.metricLabel}</span>
            {row.delta != null && metric === 'composite' && (
              <span className={cn('block text-[11px]', row.delta >= 0 ? 'text-emerald-700' : row.delta > -5 ? 'text-amber-700' : 'text-red-700')}>
                {row.delta >= 0 ? '+' : ''}{row.delta.toFixed(1)}
              </span>
            )}
          </span>
          {depth === 0 && (
            <span
              role="button"
              tabIndex={0}
              title="Open these patients in the worklist"
              className="invisible rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-900 group-hover:visible"
              onClick={(e) => { e.stopPropagation(); openInPatients(row); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openInPatients(row); } }}
            >
              <ClipboardList className="h-4 w-4" />
            </span>
          )}
        </span>
      </button>
    );
  };

  return (
    <div>
      <AcoHeader title="TIN & NPI Performance" subtitle="Participant practices and clinicians against the ACO's APP Plus composite" showReportBar />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/programs/app-plus/insights')}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Insights
        </Button>
        <div className="flex items-center gap-1 rounded-lg border bg-white p-1">
          <Button variant={view === 'explore' ? 'default' : 'ghost'} size="sm" onClick={() => setView('explore')}>
            <Compass className="mr-1.5 h-4 w-4" /> Explore
          </Button>
          <Button variant={view === 'bars' ? 'default' : 'ghost'} size="sm" onClick={() => setView('bars')}>
            <BarChart3 className="mr-1.5 h-4 w-4" /> Bars
          </Button>
          <Button variant={view === 'table' ? 'default' : 'ghost'} size="sm" onClick={() => setView('table')}>
            <Table2 className="mr-1.5 h-4 w-4" /> Table
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5">
          <span className="hidden text-xs font-medium text-slate-500 sm:inline">Collection type</span>
          <Select value={lane ?? undefined} onValueChange={setLaneChoice} disabled={lanes.length === 0}>
            <SelectTrigger className="h-9 w-[170px]"><SelectValue placeholder="No calculated data" /></SelectTrigger>
            <SelectContent>{lanes.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="hidden text-xs font-medium text-slate-500 sm:inline">Bars show</span>
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="h-9 w-[260px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="composite">Composite (all measures)</SelectItem>
              {clinical.map(c => (
                <SelectItem key={c.baseId} value={c.baseId}>
                  <span className="font-mono font-semibold">{c.pipelineId}</span> · {c.name.length > 34 ? `${c.name.slice(0, 34)}…` : c.name}{c.isInverse ? ' (inverse)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="hidden text-xs font-medium text-slate-500 sm:inline">Show</span>
          <Select value={show} onValueChange={(v: ShowFilter) => setShow(v)}>
            <SelectTrigger className="h-9 w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="below_qps">Below QPS threshold</SelectItem>
              <SelectItem value="below_aco">Below ACO composite</SelectItem>
              <SelectItem value="qps">QPs</SelectItem>
              <SelectItem value="no_claims">No claims (roster only)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="TIN, NPI, practice, clinician…" className="h-9 w-[240px] pl-8" />
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => exportCsv('tin')} disabled={!data}><Download className="mr-1.5 h-3.5 w-3.5" /> TIN CSV</Button>
          <Button variant="outline" size="sm" onClick={() => exportCsv('npi')} disabled={!data}><Download className="mr-1.5 h-3.5 w-3.5" /> NPI CSV</Button>
        </div>
      </div>

      {/* Baseline strip */}
      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-1 py-3 text-sm">
          <span><span className="text-muted-foreground">ACO composite</span> <span className="font-semibold tabular-nums">{num(acoComposite, 2)}</span>{constants && <span className="text-muted-foreground"> · QPS {constants.qpsThreshold}</span>}</span>
          <span><span className="text-muted-foreground">TINs</span> <span className="font-semibold tabular-nums">{data?.tins.length ?? '—'}</span>
            {data && qpsThreshold != null && tinRows.length > 0 && (
              <span className="text-muted-foreground"> · <span className={cn('font-semibold', tinRows.filter(t => t.composite != null && t.composite >= qpsThreshold).length === 0 ? 'text-red-700' : 'text-emerald-700')}>{tinRows.filter(t => t.composite != null && t.composite >= qpsThreshold).length}</span> meet QPS</span>
            )}
          </span>
          <span><span className="text-muted-foreground">Clinicians</span> <span className="font-semibold tabular-nums">{data ? totalNpis : '—'}</span></span>
          <span><span className="text-muted-foreground">Lane</span> <span className="font-semibold">{lane ?? '—'}</span></span>
          {data && !data.rosterImported && <span className="text-xs text-amber-700">Roster not imported — showing raw TIN / NPI ids. Import the ACO eligibility file in Configuration → CMS Reports for names, QP status and must-report class.</span>}
          <span className="ml-auto flex items-center gap-3 text-[11px] text-muted-foreground">
            {metric === 'composite' ? (
              <>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-emerald-500" /> meets QPS</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-amber-400" /> above ACO, below QPS</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-red-500" /> below ACO</span>
                <span className="flex items-center gap-1"><span className="inline-block h-3 w-0.5 bg-slate-900" /> QPS</span>
                <span className="flex items-center gap-1"><span className="inline-block h-3 w-0 border-l-2 border-dashed border-sky-600" /> ACO</span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-emerald-500" /> ≥ ACO rate</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-amber-400" /> within 5 pts</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-red-500" /> &gt;5 below</span>
                <span className="flex items-center gap-1"><span className="inline-block h-3 w-0 border-l-2 border-dashed border-sky-600" /> ACO</span>
              </>
            )}
          </span>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 rounded-md" />)}</div>
      ) : !data || data.tins.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">
          No calculated outcomes for this ACO{lane ? ` under ${lane}` : ''}. Upload claims (with the NPI column mapped) to populate this report.
        </CardContent></Card>
      ) : view === 'explore' ? (
        <ExploreView
          tins={tinRows}
          npisByTin={npiRowsByTin}
          qps={qpsThreshold}
          aco={acoComposite}
          acoCalculated={scoring.calculated as Record<string, Partial<Record<string, { met: number; not: number }>>>}
          lane={lane}
          dcThreshold={constants?.dataCompletenessThreshold ?? null}
          search={search}
          shadowPracticeId={data.shadowPracticeId}
          openInPatients={openInPatients}
          navigate={navigate}
        />
      ) : view === 'bars' ? (
        <Card>
          <CardContent className="p-2">
            <div className="mb-1 grid px-2 text-[11px] uppercase tracking-wide text-muted-foreground" style={{ gridTemplateColumns: '260px 1fr 116px' }}>
              <span>TIN / practice</span>
              <span className="flex justify-between"><span>0</span><span>{scaleMax / 2}</span><span>{scaleMax}</span></span>
              <span className="text-right">{metric === 'composite' ? 'Composite' : 'Rate'}</span>
            </div>
            <div className="space-y-0.5">
              {visibleTins.map(({ tin, kids }) => (
                <div key={tin.key}>
                  <Bar row={tin} depth={0} />
                  {expanded[tin.tin] && (
                    <div className="mb-1 space-y-0.5 border-l-2 border-slate-200 ml-4">
                      {kids.length === 0
                        ? <p className="py-1 pl-8 text-xs text-muted-foreground">No clinicians match the current filter.</p>
                        : kids.map(n => <Bar key={n.key} row={n} depth={1} />)}
                    </div>
                  )}
                </div>
              ))}
              {visibleTins.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">Nothing matches the current filters.</p>}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          {/* The card scrolls internally so the header can stick; the page itself does not scroll the grid. */}
          <CardContent className="max-h-[calc(100vh-300px)] overflow-auto p-0">
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-100 text-left text-[13px] font-semibold text-slate-700 shadow-[inset_0_-1px_0_theme(colors.slate.300)]">
                  <th className="px-3 py-3">TIN / practice</th>
                  <th className="px-2 py-3 text-right">Patients</th>
                  {clinical.map(c => (
                    <th key={c.baseId} className="px-2 py-3 text-right" title={c.name}>
                      <span className="font-mono">{c.pipelineId}</span>{c.isInverse ? <span className="ml-0.5 text-[10px] font-normal text-slate-500">inv</span> : ''}
                    </th>
                  ))}
                  <th className="px-2 py-3 text-right">Composite</th>
                  <th className="px-3 py-3 text-right">Δ ACO</th>
                </tr>
              </thead>
              <tbody>
                {visibleTins.map(({ tin, kids }) => {
                  const open = !!expanded[tin.tin];
                  const compTone = tin.composite == null ? '' : qpsThreshold != null && tin.composite >= qpsThreshold ? 'text-emerald-700' : tin.delta != null && tin.delta >= 0 ? 'text-amber-700' : 'text-red-700';
                  return (
                    <Fragment key={tin.key}>
                      <tr
                        className={cn('cursor-pointer bg-white hover:bg-slate-50', open && 'bg-slate-50')}
                        onClick={() => setExpanded(e => ({ ...e, [tin.tin]: !e[tin.tin] }))}
                      >
                        <td className={cn('border-b px-3 py-2.5', open && 'border-b-0')}>
                          <span className="flex items-center gap-1.5 text-[15px] font-semibold">
                            {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                            {tin.label}
                          </span>
                          <span className="block pl-[22px] text-xs text-muted-foreground">{tin.sublabel}</span>
                        </td>
                        <td className={cn('border-b px-2 py-2.5 text-right tabular-nums', open && 'border-b-0')}>{tin.patients.toLocaleString()}</td>
                        {tin.measures.map(m => (
                          <td key={m.baseId} className={cn('border-b px-2 py-2.5 text-right tabular-nums', open && 'border-b-0', m.dc != null && constants && m.dc < constants.dataCompletenessThreshold && 'text-amber-700')} title={`DC ${pct(m.dc)} · ${m.counts?.den ?? 0} den · ${m.openGaps} open`}>
                            {pct(m.rate)}
                          </td>
                        ))}
                        <td className={cn('border-b px-2 py-2.5 text-right text-[15px] font-bold tabular-nums', open && 'border-b-0', compTone)}>{num(tin.composite, 2)}</td>
                        <td className={cn('border-b px-3 py-2.5 text-right tabular-nums', open && 'border-b-0', tin.delta != null && (tin.delta >= 0 ? 'text-emerald-700' : 'text-red-700'))}>
                          <span className="inline-flex items-center gap-1">
                            {tin.delta != null ? `${tin.delta >= 0 ? '+' : ''}${tin.delta.toFixed(1)}` : '—'}
                            <button type="button" title="Open these patients in the worklist" className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-900"
                              onClick={(e) => { e.stopPropagation(); openInPatients(tin); }}>
                              <ClipboardList className="h-4 w-4" />
                            </button>
                          </span>
                        </td>
                      </tr>

                      {/* Expanded clinician block: a visibly different band — blue left rail,
                          tinted background, its own caption row, smaller type — so it never
                          reads as more TIN rows. */}
                      {open && (
                        <tr className="bg-sky-50/70">
                          <td colSpan={clinical.length + 4} className="border-l-4 border-sky-400 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-sky-800">
                            Clinicians in {tin.label} · {kids.length}
                          </td>
                        </tr>
                      )}
                      {open && kids.length === 0 && (
                        <tr className="bg-sky-50/40">
                          <td colSpan={clinical.length + 4} className="border-b border-l-4 border-sky-400 px-3 py-2 pl-9 text-xs text-muted-foreground">No clinicians match the current filter.</td>
                        </tr>
                      )}
                      {open && kids.map((n, idx) => {
                        const last = idx === kids.length - 1;
                        const nTone = n.composite == null ? '' : qpsThreshold != null && n.composite >= qpsThreshold ? 'text-emerald-700' : n.delta != null && n.delta >= 0 ? 'text-amber-700' : 'text-red-700';
                        return (
                          <tr key={n.key} className="cursor-pointer bg-sky-50/40 hover:bg-sky-100/60" onClick={() => setDrawer(n)}>
                            <td className={cn('border-l-4 border-sky-400 px-3 py-1.5 pl-9', last ? 'border-b-2 border-b-sky-200' : 'border-b border-b-sky-100')}>
                              <span className="flex items-center gap-1.5 text-[13px]">
                                <Users className="h-3.5 w-3.5 text-sky-600" />
                                {n.label}
                                {n.qpStatus && (n.qpStatus === 'Y' || n.qpStatus === 'P') && <Badge variant="outline" className="h-4 border-sky-300 px-1 text-[10px] text-sky-800">QP</Badge>}
                              </span>
                              <span className="block pl-5 text-[11px] text-muted-foreground">{n.sublabel}</span>
                            </td>
                            <td className={cn('px-2 py-1.5 text-right text-[13px] tabular-nums', last ? 'border-b-2 border-b-sky-200' : 'border-b border-b-sky-100')}>{n.patients.toLocaleString()}</td>
                            {n.measures.length === 0
                              ? <td colSpan={clinical.length} className={cn('px-2 py-1.5 text-center text-xs text-muted-foreground', last ? 'border-b-2 border-b-sky-200' : 'border-b border-b-sky-100')}>no claims in this lane</td>
                              : n.measures.map(m => (
                                <td key={m.baseId} className={cn('px-2 py-1.5 text-right text-[13px] tabular-nums', last ? 'border-b-2 border-b-sky-200' : 'border-b border-b-sky-100', m.dc != null && constants && m.dc < constants.dataCompletenessThreshold && 'text-amber-700')} title={`DC ${pct(m.dc)} · ${m.counts?.den ?? 0} den · ${m.openGaps} open`}>
                                  {pct(m.rate)}
                                </td>
                              ))}
                            <td className={cn('px-2 py-1.5 text-right text-[13px] font-semibold tabular-nums', last ? 'border-b-2 border-b-sky-200' : 'border-b border-b-sky-100', nTone)}>{num(n.composite, 2)}</td>
                            <td className={cn('px-3 py-1.5 text-right text-[13px] tabular-nums', last ? 'border-b-2 border-b-sky-200' : 'border-b border-b-sky-100', n.delta != null && (n.delta >= 0 ? 'text-emerald-700' : 'text-red-700'))}>{n.delta != null ? `${n.delta >= 0 ? '+' : ''}${n.delta.toFixed(1)}` : '—'}</td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Users className="h-3.5 w-3.5" />
        Read-time roll-up over per-clinician outcomes (Phase 3 dedup rules; a patient seen by two clinicians of one TIN counts once for that TIN).
        Row composites substitute the row's clinical counts into the APP engine; CAHPS and admin-claims measures stay at ACO level.
      </p>

      {/* Clinician / TIN detail drawer */}
      <Sheet open={!!drawer} onOpenChange={o => { if (!o) setDrawer(null); }}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          {drawer && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {drawer.label}
                  {drawer.qpStatus && (drawer.qpStatus === 'Y' || drawer.qpStatus === 'P') && <Badge variant="outline">QP</Badge>}
                </SheetTitle>
                <SheetDescription>
                  {drawer.kind === 'npi' ? `NPI ${drawer.id} · TIN ${drawer.tin}` : `TIN ${drawer.tin}`} · {drawer.patients.toLocaleString()} patients · {lane}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Composite</div><div className="text-2xl font-semibold tabular-nums">{num(drawer.composite, 2)}</div></div>
                <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Δ vs ACO</div><div className={cn('text-2xl font-semibold tabular-nums', drawer.delta != null && (drawer.delta >= 0 ? 'text-emerald-700' : 'text-red-700'))}>{drawer.delta != null ? `${drawer.delta >= 0 ? '+' : ''}${drawer.delta.toFixed(1)}` : '—'}</div></div>
                <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Open gaps</div><div className="text-2xl font-semibold tabular-nums">{drawer.measures.reduce((s, m) => s + m.openGaps, 0).toLocaleString()}</div></div>
              </div>

              <div className="mt-4 space-y-3">
                {drawer.measures.length === 0 && <p className="text-sm text-muted-foreground">No claims for this clinician in the {lane} lane.</p>}
                {drawer.measures.map(m => {
                  const acoCounts = scoring.calculated[m.baseId]?.[lane ?? ''];
                  const acoPerf = acoCounts ? acoCounts.met + acoCounts.not : 0;
                  const acoRate = acoPerf > 0 && acoCounts ? (acoCounts.met / acoPerf) * 100 : null;
                  const acoFav = acoRate == null ? null : (m.isInverse ? 100 - acoRate : acoRate);
                  return (
                    <div key={m.baseId} className="rounded-md border p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium"><span className="font-mono">{m.pipelineId}</span> <span className="text-muted-foreground">— {m.name}</span></div>
                          <div className="text-[11px] text-muted-foreground">
                            {m.counts ? `${m.counts.den} den · ${m.counts.met} met · ${m.counts.not} not met · ${m.counts.exc} exc · ${m.counts.ex} excl` : 'no rows'}
                            {m.isInverse ? ' · inverse (lower is better)' : ''}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-semibold tabular-nums">{pct(m.rate)}</div>
                          <div className="text-[11px] text-muted-foreground">{m.points != null ? `${m.points.toFixed(1)} pts` : m.state ? m.state.replace(/_/g, ' ') : ''}</div>
                        </div>
                      </div>
                      <div className="relative mt-2 h-2 w-full overflow-hidden rounded bg-slate-100">
                        <div className={cn('h-full rounded', m.favorable != null && acoFav != null ? (m.favorable >= acoFav ? 'bg-emerald-500' : m.favorable >= acoFav - 5 ? 'bg-amber-400' : 'bg-red-500') : 'bg-slate-300')} style={{ width: `${m.favorable ?? 0}%` }} />
                        {acoFav != null && <div className="absolute inset-y-0 w-0 border-l-2 border-dashed border-sky-600" style={{ left: `${acoFav}%` }} />}
                      </div>
                      <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
                        <span className={cn(m.dc != null && constants && m.dc < constants.dataCompletenessThreshold && 'font-medium text-amber-700')}>DC {pct(m.dc)}{constants ? ` (need ${constants.dataCompletenessThreshold}%)` : ''}</span>
                        <span>{m.openGaps} open gaps</span>
                        <span>ACO {pct(acoRate)}</span>
                      </div>
                      {drawer.kind === 'npi' && data?.shadowPracticeId && drawer.id !== '0000000000' && (
                        <Button variant="link" size="sm" className="mt-1 h-auto px-0 text-xs"
                          onClick={() => navigate(`/programs/app-plus/measure/${encodeURIComponent(`${m.pipelineId}:${lane}`)}?evaluationType=NPI&npi=${encodeURIComponent(drawer.id)}&practiceId=${data.shadowPracticeId}`)}>
                          Detailed results for this clinician <ExternalLink className="ml-1 h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 flex gap-2">
                <Button size="sm" onClick={() => openInPatients(drawer)}>
                  <ClipboardList className="mr-1.5 h-4 w-4" />
                  Open in Patients{drawer.kind === 'npi' ? ' — this clinician' : ' — this TIN'}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
